import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import SupervisorFields from 'components/supervisor/SupervisorFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import validateCreateSupervisorForm from 'common/validations/form/createSupervisor';
import { createSupervisorInitialValues } from 'common/form/initial-values/create';
import { generatePassword } from 'common/form/utils';
import getDataToSave from 'common/form/dataToSave';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import supervisorSlice from 'store/modules/supervisor';

const requiredFields = ['first_name', 'last_name', 'email', 'birthdate', 'ui_language', 'password'];

function SupervisorCreate() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('supervisor.create', { ns: 'routes' });
  const formRef = useRef(null);
  const status = useSelector((state) => state.supervisor.status.general);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  useEffect(() => {
    if (status === requestStatus.success) {
      formRef.current.restart();
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = getDataToSave(values, 'user');
      dispatch(supervisorSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createSupervisorInitialValues}
        mutators={{
          generatePassword
        }}
        onSubmit={onCreate}
        validate={validateCreateSupervisorForm}
        render={({ form, handleSubmit, invalid, pristine }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <SupervisorFields form={form} requiredFields={requiredFields} />
              <Box sx={{ marginTop: '60px', textAlign: 'right' }}>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listSupervisor.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('supervisor.create', { ns: 'buttons' })}
                  type="submit"
                />
              </Box>
            </form>
          );
        }}
      />
    </>
  );
}

export default SupervisorCreate;
